import {escapeHtml as escapePrintValue} from "../../utils/escapeHtml";
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const lower = (value) =>
  clean(value).toLowerCase();

const normalizeRoom = (value) =>
  clean(value).toUpperCase();

const getDateObject = (value) => {
  if (!value) {
    return null;
  }

  if (
    typeof value?.toDate ===
    "function"
  ) {
    return value.toDate();
  }

  if (value?.seconds) {
    return new Date(
      value.seconds * 1000
    );
  }

  const date = new Date(value);

  return Number.isNaN(
    date.getTime()
  )
    ? null
    : date;
};

const getTimestamp = (value) =>
  getDateObject(value)?.getTime() || 0;

const formatDateTime = (value) => {
  const date = getDateObject(value);

  if (!date) {
    return clean(value) || "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTodayInputValue = () => {
  const date = new Date();

  return [
    date.getFullYear(),
    String(
      date.getMonth() + 1
    ).padStart(2, "0"),
    String(
      date.getDate()
    ).padStart(2, "0"),
  ].join("-");
};

const isSameLocalDate = (
  value,
  dateKey
) => {
  const date = getDateObject(value);

  if (!date || !dateKey) {
    return false;
  }

  const localDateKey = [
    date.getFullYear(),
    String(
      date.getMonth() + 1
    ).padStart(2, "0"),
    String(
      date.getDate()
    ).padStart(2, "0"),
  ].join("-");

  return localDateKey === dateKey;
};

const ACTION_OPTIONS = [
  {value: "food", label: "Food Orders / Menu"},
  {
    value: "all",
    label: "All Actions",
  },
  {
    value: "check_in",
    label: "Check-In",
  },
  {
    value: "checkout",
    label: "Checkout",
  },
  {
    value: "payment",
    label: "Payment",
  },
  {
    value: "expense",
    label: "Expense",
  },
  {
    value: "guest_profile",
    label: "Guest Profile",
  },
  {
    value: "housekeeping",
    label: "Housekeeping",
  },
  {
    value: "maintenance",
    label: "Maintenance",
  },
  {
    value: "room_board",
    label: "Room Board",
  },
  {
    value: "discount",
    label: "Discount / GST",
  },
];

const normalizeAction = (value) => {
  const action = lower(value);
  if (action.startsWith("food_")) return "food";

  if (
    action.includes("check_in") ||
    action.includes("check-in") ||
    action.includes("checkin")
  ) {
    return "check_in";
  }

  if (
    action.includes("checkout") ||
    action.includes("check_out") ||
    action.includes("check-out")
  ) {
    return "checkout";
  }

  if (
    action.includes("payment") ||
    action.includes("receipt")
  ) {
    return "payment";
  }

  if (
    action.includes("expense")
  ) {
    return "expense";
  }

  if (
    action.includes("guest") ||
    action.includes("profile") ||
    action.includes("crm")
  ) {
    return "guest_profile";
  }

  if (
    action.includes("housekeeping") ||
    action.includes("cleaning") ||
    action.includes("room_ready")
  ) {
    return "housekeeping";
  }

  if (
    action.includes("maintenance") ||
    action.includes("repair")
  ) {
    return "maintenance";
  }

  if (
    action.includes("room_board") ||
    action.includes("room status")
  ) {
    return "room_board";
  }

  if (
    action.includes("discount") ||
    action.includes("gst")
  ) {
    return "discount";
  }

  return action || "other";
};

const getLogAction = (log = {}) =>
  normalizeAction(
    log.action ||
      log.actionType ||
      log.eventType ||
      log.type
  );

const getLogTime = (log = {}) =>
  log.createdAt ||
  log.eventAt ||
  log.updatedAt ||
  log.timestamp ||
  "";

const getLogTitle = (log = {}) =>
  clean(
    log.title ||
      log.actionTitle ||
      log.description ||
      log.message
  ) || "Activity";

const getLogDescription = (log = {}) =>
  clean(
    log.description ||
      log.details ||
      log.note ||
      log.message
  );

const getActorName = (log = {}) =>
  clean(
    log.actorName ||
      log.performedBy ||
      log.managerName ||
      log.userName ||
      log.createdByName ||
      log.actorEmail ||
      log.createdBy
  ) || "Manager";

const getLogSearchText = (log = {}) =>
  [
    log.title,
    log.action,
    log.actionType,
    log.eventType,
    log.description,
    log.details,
    log.note,
    log.message,
    log.room,
    log.guestName,
    log.phoneNumber,
    log.stayId,
    log.invoiceNumber,
    log.receiptNumber,
    log.actorName,
    log.performedBy,
    log.managerName,
    log.actorEmail,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

/* -------------------------------------------------------------------------- */
/*                              Audit Log Panel                               */
/* -------------------------------------------------------------------------- */

function AuditLogPanel({
  hotelId,
  hotelName,
}) {
  const [logs, setLogs] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [loadError, setLoadError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [
    selectedDate,
    setSelectedDate,
  ] = useState(
    getTodayInputValue()
  );

  const [
    actionFilter,
    setActionFilter,
  ] = useState("all");

  const [
    actorFilter,
    setActorFilter,
  ] = useState("all");

  const [
    roomFilter,
    setRoomFilter,
  ] = useState("all");

  const [sortBy, setSortBy] =
    useState("recent");

  const [
    selectedLog,
    setSelectedLog,
  ] = useState(null);

  /* ------------------------------------------------------------------------ */
  /*                           Firestore Listener                              */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!hotelId) {
      setLogs([]);
      setLoading(false);

      setLoadError(
        "Hotel ID missing hai."
      );

      return undefined;
    }

    setLoading(true);
    setLoadError("");

    const auditQuery = query(
      collection(db, "auditLogs"),

      where(
        "hotelId",
        "==",
        hotelId
      )
    );

    return onSnapshot(
      auditQuery,

      (snapshot) => {
        const list = snapshot.docs
          .map((logDocument) => ({
            id: logDocument.id,
            ...logDocument.data(),
          }))
          .sort(
            (firstLog, secondLog) =>
              getTimestamp(
                getLogTime(secondLog)
              ) -
              getTimestamp(
                getLogTime(firstLog)
              )
          );

        setLogs(list);
        setLoading(false);
        setLoadError("");
      },

      (error) => {
        console.error(
          "Audit logs load error:",
          error
        );

        setLogs([]);
        setLoading(false);

        setLoadError(
          error?.message ||
            "Audit logs load nahi hue."
        );
      }
    );
  }, [hotelId]);

  /* ------------------------------------------------------------------------ */
  /*                              Dropdowns                                    */
  /* ------------------------------------------------------------------------ */

  const actorOptions = useMemo(
    () =>
      Array.from(
        new Set(
          logs
            .map((log) =>
              getActorName(log)
            )
            .filter(Boolean)
        )
      ).sort((first, second) =>
        first.localeCompare(second)
      ),
    [logs]
  );

  const roomOptions = useMemo(
    () =>
      Array.from(
        new Set(
          logs
            .map((log) =>
              normalizeRoom(log.room)
            )
            .filter(Boolean)
        )
      ).sort((first, second) =>
        first.localeCompare(
          second,
          undefined,
          {
            numeric: true,
          }
        )
      ),
    [logs]
  );

  /* ------------------------------------------------------------------------ */
  /*                               Summary                                     */
  /* ------------------------------------------------------------------------ */

  const summary = useMemo(() => {
    const selectedDateLogs =
      logs.filter((log) =>
        selectedDate
          ? isSameLocalDate(
              getLogTime(log),
              selectedDate
            )
          : true
      );

    return {
      total: selectedDateLogs.length,

      checkIns:
        selectedDateLogs.filter(
          (log) =>
            getLogAction(log) ===
            "check_in"
        ).length,

      checkouts:
        selectedDateLogs.filter(
          (log) =>
            getLogAction(log) ===
            "checkout"
        ).length,

      payments:
        selectedDateLogs.filter(
          (log) =>
            getLogAction(log) ===
            "payment"
        ).length,

      expenses:
        selectedDateLogs.filter(
          (log) =>
            getLogAction(log) ===
            "expense"
        ).length,

      housekeeping:
        selectedDateLogs.filter(
          (log) =>
            getLogAction(log) ===
            "housekeeping"
        ).length,

      maintenance:
        selectedDateLogs.filter(
          (log) =>
            getLogAction(log) ===
            "maintenance"
        ).length,

      actors: new Set(
        selectedDateLogs.map(
          (log) =>
            getActorName(log)
        )
      ).size,
    };
  }, [logs, selectedDate]);

  /* ------------------------------------------------------------------------ */
  /*                          Filter and Sorting                               */
  /* ------------------------------------------------------------------------ */

  const filteredLogs = useMemo(() => {
    const searchTerm =
      lower(search);

    const filtered = logs.filter(
      (log) => {
        const logAction =
          getLogAction(log);

        const actorName =
          getActorName(log);

        const room =
          normalizeRoom(log.room);

        const matchesDate =
          !selectedDate ||
          isSameLocalDate(
            getLogTime(log),
            selectedDate
          );

        const matchesSearch =
          !searchTerm ||
          getLogSearchText(
            log
          ).includes(searchTerm);

        const matchesAction =
          actionFilter === "all" ||
          logAction ===
            actionFilter;

        const matchesActor =
          actorFilter === "all" ||
          actorName ===
            actorFilter;

        const matchesRoom =
          roomFilter === "all" ||
          room === roomFilter;

        return (
          matchesDate &&
          matchesSearch &&
          matchesAction &&
          matchesActor &&
          matchesRoom
        );
      }
    );

    return [...filtered].sort(
      (firstLog, secondLog) => {
        if (sortBy === "oldest") {
          return (
            getTimestamp(
              getLogTime(firstLog)
            ) -
            getTimestamp(
              getLogTime(secondLog)
            )
          );
        }

        if (sortBy === "action") {
          return getLogAction(
            firstLog
          ).localeCompare(
            getLogAction(secondLog)
          );
        }

        if (sortBy === "actor") {
          return getActorName(
            firstLog
          ).localeCompare(
            getActorName(secondLog)
          );
        }

        if (sortBy === "room") {
          return normalizeRoom(
            firstLog.room
          ).localeCompare(
            normalizeRoom(
              secondLog.room
            ),
            undefined,
            {
              numeric: true,
            }
          );
        }

        return (
          getTimestamp(
            getLogTime(secondLog)
          ) -
          getTimestamp(
            getLogTime(firstLog)
          )
        );
      }
    );
  }, [
    logs,
    search,
    selectedDate,
    actionFilter,
    actorFilter,
    roomFilter,
    sortBy,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                                  Print                                    */
  /* ------------------------------------------------------------------------ */

  const printAuditLogs = () => {
    const printWindow = window.open(
      "",
      "_blank",
      "width=1250,height=900"
    );

    if (!printWindow) {
      alert(
        "Print popup block ho gaya. Browser me popups allow karo."
      );

      return;
    }

    const rows = filteredLogs
      .map(
        (log, index) => `
          <tr>
            <td>${index + 1}</td>

            <td>
              ${escapePrintValue(clean(
                formatDateTime(
                  getLogTime(log)
                )
              ))}
            </td>

            <td>
              ${escapePrintValue(clean(
                getActionLabel(
                  getLogAction(log)
                )
              ))}
            </td>

            <td>
              ${escapePrintValue(clean(
                log.room || "-"
              ))}
            </td>

            <td>
              ${escapePrintValue(clean(
                log.guestName || "-"
              ))}
            </td>

            <td>
              ${escapePrintValue(clean(
                getActorName(log)
              ))}
            </td>

            <td>
              ${escapePrintValue(clean(
                getLogTitle(log)
              ))}
            </td>

            <td>
              ${escapePrintValue(clean(
                getLogDescription(
                  log
                ) || "-"
              ))}
            </td>

            <td>
              ${escapePrintValue(clean(
                log.referenceId ||
                  log.stayId ||
                  log.invoiceNumber ||
                  log.receiptNumber ||
                  "-"
              ))}
            </td>
          </tr>
        `
      )
      .join("");

    printWindow.document.open();

    printWindow.document.write(`
      <!DOCTYPE html>

      <html>
        <head>
          <meta charset="UTF-8" />

          <title>
            Audit Log ${selectedDate}
          </title>

          <style>
            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              padding: 8mm;
              color: #0f172a;
              font-family: Arial, sans-serif;
            }

            h1 {
              margin: 0;
              text-align: center;
              font-size: 20px;
            }

            .subtitle {
              margin-top: 5px;
              text-align: center;
              color: #475569;
              font-size: 10px;
            }

            .summary {
              display: grid;
              grid-template-columns:
                repeat(6,1fr);
              gap: 6px;
              margin: 12px 0;
            }

            .summary div {
              padding: 7px;
              border: 1px solid #94a3b8;
              text-align: center;
              font-size: 8px;
            }

            .summary strong {
              display: block;
              margin-top: 4px;
              font-size: 12px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            th,
            td {
              padding: 5px;
              border: 1px solid #94a3b8;
              font-size: 7px;
              vertical-align: top;
            }

            th {
              background: #e2e8f0;
              text-align: left;
            }

            @page {
              size: A4 landscape;
              margin: 6mm;
            }
          </style>
        </head>

        <body>
          <h1>
            ${escapePrintValue(clean(
              hotelName || hotelId
            ))} — AUDIT LOG
          </h1>

          <div class="subtitle">
            Date: ${selectedDate || "All"}
          </div>

          <div class="summary">
            <div>
              Total Actions
              <strong>
                ${summary.total}
              </strong>
            </div>

            <div>
              Check-Ins
              <strong>
                ${summary.checkIns}
              </strong>
            </div>

            <div>
              Checkouts
              <strong>
                ${summary.checkouts}
              </strong>
            </div>

            <div>
              Payments
              <strong>
                ${summary.payments}
              </strong>
            </div>

            <div>
              Expenses
              <strong>
                ${summary.expenses}
              </strong>
            </div>

            <div>
              Active Users
              <strong>
                ${summary.actors}
              </strong>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Date & Time</th>
                <th>Action</th>
                <th>Room</th>
                <th>Guest</th>
                <th>Performed By</th>
                <th>Title</th>
                <th>Description</th>
                <th>Reference</th>
              </tr>
            </thead>

            <tbody>
              ${rows}
            </tbody>
          </table>

          <script>
            window.onload = function () {
              setTimeout(function () {
                window.focus();
                window.print();
              }, 300);
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <section style={sectionStyle}>
      <header style={headerStyle}>
        <div>
          <h3 style={titleStyle}>
            🛡 Audit Log
          </h3>

          <div style={subtitleStyle}>
            Manager activities, room
            operations aur financial changes
          </div>
        </div>

        <div style={headerActionsStyle}>
          <input
            type="date"
            value={selectedDate}
            onChange={(event) =>
              setSelectedDate(
                event.target.value
              )
            }
            style={inputStyle}
          />

          <button
            type="button"
            onClick={printAuditLogs}
            style={printButtonStyle}
          >
            🖨 Print Audit Log
          </button>
        </div>
      </header>

      <div style={summaryGridStyle}>
        <SummaryCard
          label="Total Actions"
          value={summary.total}
        />

        <SummaryCard
          label="Check-Ins"
          value={summary.checkIns}
        />

        <SummaryCard
          label="Checkouts"
          value={summary.checkouts}
        />

        <SummaryCard
          label="Payments"
          value={summary.payments}
        />

        <SummaryCard
          label="Expenses"
          value={summary.expenses}
        />

        <SummaryCard
          label="Housekeeping"
          value={summary.housekeeping}
        />

        <SummaryCard
          label="Maintenance"
          value={summary.maintenance}
        />

        <SummaryCard
          label="Active Users"
          value={summary.actors}
        />
      </div>

      <div style={toolbarStyle}>
        <input
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
          placeholder="Room, guest, action, invoice, receipt, manager..."
          style={inputStyle}
        />

        <select
          value={actionFilter}
          onChange={(event) =>
            setActionFilter(
              event.target.value
            )
          }
          style={inputStyle}
        >
          {ACTION_OPTIONS.map(
            (option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            )
          )}
        </select>

        <select
          value={actorFilter}
          onChange={(event) =>
            setActorFilter(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="all">
            All Managers
          </option>

          {actorOptions.map(
            (actor) => (
              <option
                key={actor}
                value={actor}
              >
                {actor}
              </option>
            )
          )}
        </select>

        <select
          value={roomFilter}
          onChange={(event) =>
            setRoomFilter(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="all">
            All Rooms
          </option>

          {roomOptions.map(
            (room) => (
              <option
                key={room}
                value={room}
              >
                Room {room}
              </option>
            )
          )}
        </select>

        <select
          value={sortBy}
          onChange={(event) =>
            setSortBy(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="recent">
            Recent First
          </option>

          <option value="oldest">
            Oldest First
          </option>

          <option value="action">
            Action Type
          </option>

          <option value="actor">
            Manager Name
          </option>

          <option value="room">
            Room Number
          </option>
        </select>
      </div>

      {loadError && (
        <div style={errorBoxStyle}>
          {loadError}
        </div>
      )}

      {loading ? (
        <div style={emptyBoxStyle}>
          Audit logs loading...
        </div>
      ) : filteredLogs.length === 0 ? (
        <div style={emptyBoxStyle}>
          Selected filter ke liye audit
          record nahi mila.
        </div>
      ) : (
        <div style={timelineStyle}>
          {filteredLogs.map((log) => (
            <button
              key={log.id}
              type="button"
              onClick={() =>
                setSelectedLog(log)
              }
              style={logCardStyle}
            >
              <div style={timelineDotWrapperStyle}>
                <div
                  style={{
                    ...timelineDotStyle,
                    background:
                      getActionColor(
                        getLogAction(log)
                      ),
                  }}
                />
              </div>

              <div style={logContentStyle}>
                <div style={logHeaderStyle}>
                  <div>
                    <div style={logTitleStyle}>
                      {getLogTitle(log)}
                    </div>

                    <div style={logMetaStyle}>
                      {formatDateTime(
                        getLogTime(log)
                      )}{" "}
                      • {getActorName(log)}
                    </div>
                  </div>

                  <ActionBadge
                    action={getLogAction(
                      log
                    )}
                  />
                </div>

                <div style={infoGridStyle}>
                  <LogInfo
                    label="Room"
                    value={
                      log.room
                        ? `Room ${normalizeRoom(
                            log.room
                          )}`
                        : "-"
                    }
                  />

                  <LogInfo
                    label="Guest"
                    value={
                      log.guestName || "-"
                    }
                  />

                  <LogInfo
                    label="Reference"
                    value={
                      log.referenceId ||
                      log.stayId ||
                      log.invoiceNumber ||
                      log.receiptNumber ||
                      "-"
                    }
                  />

                  <LogInfo
                    label="Amount"
                    value={
                      log.amount !==
                        undefined &&
                      log.amount !== null
                        ? `₹${Number(
                            log.amount || 0
                          ).toLocaleString(
                            "en-IN"
                          )}`
                        : "-"
                    }
                  />
                </div>

                {getLogDescription(log) && (
                  <div style={descriptionStyle}>
                    {getLogDescription(log)}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedLog && (
        <AuditDetailsModal
          log={selectedLog}
          onClose={() =>
            setSelectedLog(null)
          }
        />
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Audit Details Modal                             */
/* -------------------------------------------------------------------------- */

function AuditDetailsModal({
  log,
  onClose,
}) {
  return (
    <div
      style={overlayStyle}
      onMouseDown={(event) => {
        if (
          event.target ===
          event.currentTarget
        ) {
          onClose?.();
        }
      }}
    >
      <div style={modalStyle}>
        <header style={modalHeaderStyle}>
          <div>
            <div style={modalTitleRowStyle}>
              <h2 style={modalTitleStyle}>
                {getLogTitle(log)}
              </h2>

              <ActionBadge
                action={getLogAction(
                  log
                )}
              />
            </div>

            <div style={subtitleStyle}>
              {formatDateTime(
                getLogTime(log)
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() =>
              onClose?.()
            }
            style={closeButtonStyle}
          >
            ×
          </button>
        </header>

        <div style={modalGridStyle}>
          <DetailBox
            label="Performed By"
            value={getActorName(log)}
          />

          <DetailBox
            label="Room"
            value={
              log.room
                ? `Room ${normalizeRoom(
                    log.room
                  )}`
                : "-"
            }
          />

          <DetailBox
            label="Guest"
            value={
              log.guestName || "-"
            }
          />

          <DetailBox
            label="Phone"
            value={
              log.phoneNumber || "-"
            }
          />

          <DetailBox
            label="Stay ID"
            value={log.stayId || "-"}
          />

          <DetailBox
            label="Invoice"
            value={
              log.invoiceNumber || "-"
            }
          />

          <DetailBox
            label="Receipt"
            value={
              log.receiptNumber || "-"
            }
          />

          <DetailBox
            label="Amount"
            value={
              log.amount !== undefined
                ? `₹${Number(
                    log.amount || 0
                  ).toLocaleString(
                    "en-IN"
                  )}`
                : "-"
            }
          />
        </div>

        {getLogDescription(log) && (
          <section style={detailsSectionStyle}>
            <h3 style={sectionHeadingStyle}>
              Details
            </h3>

            <div style={detailsTextStyle}>
              {getLogDescription(log)}
            </div>
          </section>
        )}

        {log.before && (
          <JsonSection
            title="Before"
            data={log.before}
          />
        )}

        {log.after && (
          <JsonSection
            title="After"
            data={log.after}
          />
        )}

        {log.changes && (
          <JsonSection
            title="Changes"
            data={log.changes}
          />
        )}

        {log.metadata && (
          <JsonSection
            title="Metadata"
            data={log.metadata}
          />
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Small Components                               */
/* -------------------------------------------------------------------------- */

function getActionLabel(action) {
  const labels = {
    check_in: "Check-In",
    checkout: "Checkout",
    payment: "Payment",
    expense: "Expense",
    guest_profile: "Guest Profile",
    food: "Food Orders / Menu",
    housekeeping: "Housekeeping",
    maintenance: "Maintenance",
    room_board: "Room Board",
    discount: "Discount / GST",
    other: "Other",
  };

  return (
    labels[action] ||
    clean(action) ||
    "Other"
  );
}

function getActionColor(action) {
  const colors = {
    check_in: "#16a34a",
    checkout: "#ea580c",
    payment: "#2563eb",
    expense: "#dc2626",
    guest_profile: "#7c3aed",
    food: "#c2410c",
    housekeeping: "#0891b2",
    maintenance: "#c2410c",
    room_board: "#475569",
    discount: "#9333ea",
    other: "#64748b",
  };

  return colors[action] || colors.other;
}

function SummaryCard({
  label,
  value,
}) {
  return (
    <div style={summaryCardStyle}>
      <span style={summaryLabelStyle}>
        {label}
      </span>

      <strong style={summaryValueStyle}>
        {value}
      </strong>
    </div>
  );
}

function ActionBadge({ action }) {
  const color =
    getActionColor(action);

  return (
    <span
      style={{
        ...actionBadgeStyle,
        color,
        background: `${color}18`,
      }}
    >
      {getActionLabel(action)}
    </span>
  );
}

function LogInfo({
  label,
  value,
}) {
  return (
    <div>
      <span style={infoLabelStyle}>
        {label}
      </span>

      <strong style={infoValueStyle}>
        {value}
      </strong>
    </div>
  );
}

function DetailBox({
  label,
  value,
}) {
  return (
    <div style={detailBoxStyle}>
      <span style={detailLabelStyle}>
        {label}
      </span>

      <strong style={detailValueStyle}>
        {value}
      </strong>
    </div>
  );
}

function JsonSection({
  title,
  data,
}) {
  return (
    <section style={detailsSectionStyle}>
      <h3 style={sectionHeadingStyle}>
        {title}
      </h3>

      <pre style={jsonStyle}>
        {JSON.stringify(
          data,
          null,
          2
        )}
      </pre>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const sectionStyle = {
  marginTop: 34,
  padding: 18,

  border:
    "1px solid #e2e8f0",

  borderRadius: 18,

  background: "#ffffff",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 12,
  flexWrap: "wrap",
};

const titleStyle = {
  margin: 0,
  color: "#0f172a",
};

const subtitleStyle = {
  marginTop: 5,

  color: "#64748b",

  fontSize: 11,
};

const headerActionsStyle = {
  display: "flex",
  alignItems: "center",

  gap: 8,
  flexWrap: "wrap",
};

const summaryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(115px,1fr))",

  gap: 9,

  marginTop: 15,
};

const summaryCardStyle = {
  padding: 11,

  border:
    "1px solid #e2e8f0",

  borderRadius: 11,

  background: "#f8fafc",
};

const summaryLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 9,
  fontWeight: 800,

  textTransform: "uppercase",
};

const summaryValueStyle = {
  display: "block",

  marginTop: 4,

  color: "#0f172a",

  fontSize: 18,
  fontWeight: 900,
};

const toolbarStyle = {
  display: "grid",

  gridTemplateColumns:
    "minmax(230px,1fr) repeat(4,minmax(140px,185px))",

  gap: 8,

  marginTop: 15,
};

const inputStyle = {
  width: "100%",
  minHeight: 42,

  padding: 10,

  border:
    "1px solid #cbd5e1",

  borderRadius: 9,

  background: "#ffffff",
  color: "#0f172a",

  boxSizing: "border-box",

  outline: "none",
};

const printButtonStyle = {
  minHeight: 42,

  padding: "9px 13px",

  border: 0,
  borderRadius: 9,

  background: "#334155",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const errorBoxStyle = {
  marginTop: 12,
  padding: 11,

  border:
    "1px solid #fecaca",

  borderRadius: 10,

  background: "#fef2f2",
  color: "#b91c1c",

  fontSize: 11,
  fontWeight: 700,
};

const emptyBoxStyle = {
  marginTop: 13,
  padding: 18,

  border:
    "1px dashed #cbd5e1",

  borderRadius: 11,

  color: "#64748b",

  textAlign: "center",
};

const timelineStyle = {
  position: "relative",

  display: "flex",
  flexDirection: "column",

  gap: 10,

  marginTop: 15,
};

const logCardStyle = {
  display: "grid",

  gridTemplateColumns:
    "28px minmax(0,1fr)",

  width: "100%",

  padding: 0,

  border: 0,

  background: "transparent",

  textAlign: "left",

  cursor: "pointer",
};

const timelineDotWrapperStyle = {
  position: "relative",

  display: "flex",
  justifyContent: "center",

  paddingTop: 17,
};

const timelineDotStyle = {
  width: 12,
  height: 12,

  borderRadius: "50%",

  boxShadow:
    "0 0 0 4px #ffffff, 0 0 0 5px #cbd5e1",
};

const logContentStyle = {
  padding: 13,

  border:
    "1px solid #e2e8f0",

  borderRadius: 13,

  background: "#ffffff",

  boxShadow:
    "0 4px 14px rgba(15,23,42,.04)",
};

const logHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 10,
};

const logTitleStyle = {
  color: "#0f172a",

  fontSize: 14,
  fontWeight: 900,
};

const logMetaStyle = {
  marginTop: 4,

  color: "#64748b",

  fontSize: 10,
};

const actionBadgeStyle = {
  display: "inline-block",

  padding: "5px 8px",

  borderRadius: 999,

  fontSize: 9,
  fontWeight: 900,

  whiteSpace: "nowrap",
};

const infoGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(120px,1fr))",

  gap: 8,

  marginTop: 11,
};

const infoLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 8,
  fontWeight: 800,

  textTransform: "uppercase",
};

const infoValueStyle = {
  display: "block",

  marginTop: 3,

  color: "#334155",

  fontSize: 10,
  fontWeight: 800,

  overflowWrap: "anywhere",
};

const descriptionStyle = {
  marginTop: 10,
  paddingTop: 9,

  borderTop:
    "1px solid #e2e8f0",

  color: "#475569",

  fontSize: 11,
  lineHeight: 1.5,
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 32000,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  padding: 12,

  background:
    "rgba(15,23,42,.78)",
};

const modalStyle = {
  width: "min(800px,100%)",
  maxHeight: "95vh",

  overflowY: "auto",

  padding: 19,

  borderRadius: 20,

  background: "#ffffff",

  boxSizing: "border-box",
};

const modalHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 12,

  paddingBottom: 13,

  borderBottom:
    "1px solid #e2e8f0",
};

const modalTitleRowStyle = {
  display: "flex",
  alignItems: "center",

  gap: 8,
  flexWrap: "wrap",
};

const modalTitleStyle = {
  margin: 0,

  color: "#0f172a",
};

const closeButtonStyle = {
  width: 38,
  height: 38,

  border: 0,
  borderRadius: 10,

  background: "#f1f5f9",
  color: "#334155",

  fontSize: 25,
  cursor: "pointer",
};

const modalGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(160px,1fr))",

  gap: 9,

  marginTop: 14,
};

const detailBoxStyle = {
  padding: 10,

  border:
    "1px solid #e2e8f0",

  borderRadius: 10,

  background: "#f8fafc",
};

const detailLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 8,
  fontWeight: 800,

  textTransform: "uppercase",
};

const detailValueStyle = {
  display: "block",

  marginTop: 4,

  color: "#334155",

  fontSize: 10,
  fontWeight: 900,

  overflowWrap: "anywhere",
};

const detailsSectionStyle = {
  marginTop: 13,
  padding: 12,

  border:
    "1px solid #e2e8f0",

  borderRadius: 11,

  background: "#f8fafc",
};

const sectionHeadingStyle = {
  margin: "0 0 8px",

  color: "#0f172a",

  fontSize: 13,
};

const detailsTextStyle = {
  color: "#475569",

  fontSize: 11,
  lineHeight: 1.6,

  whiteSpace: "pre-wrap",
};

const jsonStyle = {
  maxHeight: 320,

  overflow: "auto",

  margin: 0,
  padding: 10,

  borderRadius: 8,

  background: "#0f172a",
  color: "#e2e8f0",

  fontSize: 10,
  lineHeight: 1.5,

  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
};

export default AuditLogPanel;